import {
    CaretDownIcon,
    CaretUpIcon,
    CheckCircleIcon,
    WarningCircleIcon,
    XIcon,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { CircularProgress, HorizontalProgressBar } from '@/components';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { ReportStatus, type ReportPipelineStatus } from '@/types/report';
import {
    procedureProgressPercent,
    shouldShowProcedureStatusRow,
} from '../../utils/procedureProgress';
import { ProcedureStatusRow } from './ProcedureStatusRow';

export interface ProcedureStatusCardProps {
    reportId: string;
    pipelineStatus: ReportPipelineStatus | undefined;
    pipelineStatusError: Error | undefined;
}

export function ProcedureStatusCard({
    reportId,
    pipelineStatus,
    pipelineStatusError,
}: ProcedureStatusCardProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const { t: tCommon } = useTranslation(I18nNamespace.COMMON);
    const { mutate } = useSWRConfig();
    const [expanded, setExpanded] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (
            pipelineStatus?.status !== ReportStatus.COMPLETED &&
            pipelineStatus?.status !== ReportStatus.ERROR
        ) {
            return;
        }
        void mutate(
            (key) =>
                Array.isArray(key) &&
                key[0] === CACHE_KEYS.REPORT_EVENTS_PREFIX &&
                key[1] === reportId,
        );
    }, [pipelineStatus?.status, reportId, mutate]);

    if (dismissed) {
        return null;
    }

    if (pipelineStatusError && pipelineStatus === undefined) {
        return (
            <div
                className="mt-6 rounded-lg border border-grey-500 px-4 py-3"
                data-testid="procedure-status-error"
            >
                <p className="text-sm text-grey-200">
                    {t('detail.procedureProgress.loadError')}
                </p>
            </div>
        );
    }

    if (pipelineStatus === undefined) {
        return (
            <div
                className="mt-6 rounded-lg border border-grey-500 px-4 py-4"
                data-testid="procedure-status-skeleton"
            >
                <div className="h-5 w-2/3 max-w-sm rounded bg-grey-600 animate-pulse" />
                <div className="mt-4 h-0.5 w-full rounded-full bg-grey-600 animate-pulse" />
            </div>
        );
    }

    if (pipelineStatus.sources.length === 0) {
        return null;
    }

    const progressPercent = procedureProgressPercent(pipelineStatus.sources);

    const reportDone = pipelineStatus.status === ReportStatus.COMPLETED;
    const reportFailed = pipelineStatus.status === ReportStatus.ERROR;
    const reportInFlight = !reportDone && !reportFailed;

    const summaryIcon = reportDone ? (
        <CheckCircleIcon
            className="size-5 text-success-100"
            weight="regular"
            aria-hidden
        />
    ) : reportFailed ? (
        <WarningCircleIcon
            className="size-5 text-danger-300"
            weight="regular"
            aria-hidden
        />
    ) : (
        <CircularProgress progress={progressPercent} size={4} />
    );

    const summaryLabel = reportDone
        ? t('detail.procedureProgress.reportCompleted')
        : reportFailed
          ? t('detail.procedureProgress.reportNotCompleted')
          : t('detail.procedureProgress.reportInProgress');

    const visibleSources = pipelineStatus.sources.filter(
        shouldShowProcedureStatusRow,
    );

    return (
        <div
            className="mt-6 rounded-lg border border-grey-500 px-4 py-4"
            data-testid="procedure-status-card"
        >
            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-2 text-left text-base font-medium leading-normal text-white-100 outline-none focus-visible:ring-2 focus-visible:ring-grey-400 focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900 rounded"
                        aria-expanded={expanded}
                        onClick={() => setExpanded((previous) => !previous)}
                    >
                        <span
                            className="flex size-[1lh] shrink-0 items-center justify-center"
                            aria-hidden
                        >
                            {summaryIcon}
                        </span>
                        <span className="min-w-0 flex-1">{summaryLabel}</span>
                        <span className="shrink-0 text-white-100">
                            {expanded ? (
                                <CaretUpIcon
                                    className="size-4"
                                    weight="bold"
                                    aria-hidden
                                />
                            ) : (
                                <CaretDownIcon
                                    className="size-4"
                                    weight="bold"
                                    aria-hidden
                                />
                            )}
                        </span>
                    </button>
                    {reportInFlight ? null : (
                        <button
                            type="button"
                            onClick={() => setDismissed(true)}
                            className="shrink-0 p-1 text-grey-300 hover:text-white-100 active:scale-95 transition-colors"
                            aria-label={tCommon('button.closeNotification')}
                        >
                            <XIcon className="size-4" weight="bold" />
                        </button>
                    )}
                </div>
                {expanded && reportInFlight ? (
                    <div className="mt-3">
                        <HorizontalProgressBar value={progressPercent} />
                    </div>
                ) : null}
                {expanded && visibleSources.length > 0 ? (
                    <ul className="mt-4 list-none pl-0 space-y-0">
                        {visibleSources.map((source) => (
                            <ProcedureStatusRow
                                key={source.sourceId}
                                source={source}
                            />
                        ))}
                    </ul>
                ) : null}
            </div>
        </div>
    );
}
